import { useState, useEffect } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { toast } from "@/lib/toast";
import {
  Target,
  Plus,
  Check,
  Clock,
  Flame,
  TrendingUp,
  Calendar,
  Bell,
  BellOff,
  Trash2,
  Edit,
  MoreVertical,
  Loader2,
  CheckCircle2,
  Circle,
  Award,
  BarChart3
} from "lucide-react";

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Todos los días" },
  { value: "weekdays", label: "Días de semana (Lun-Vie)" },
  { value: "weekends", label: "Fines de semana" },
  { value: "custom", label: "Días personalizados" },
];

const DAYS_OF_WEEK = [
  { value: 0, label: "Dom", short: "D" },
  { value: 1, label: "Lun", short: "L" },
  { value: 2, label: "Mar", short: "M" },
  { value: 3, label: "Mié", short: "X" },
  { value: 4, label: "Jue", short: "J" },
  { value: 5, label: "Vie", short: "V" },
  { value: 6, label: "Sáb", short: "S" },
];

const HabitsPage = () => {
  const [habits, setHabits] = useState([]);
  const [todayHabits, setTodayHabits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHabit, setEditingHabit] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: "",
    description: "",
    frequency: "daily",
    custom_days: [],
    reminder_time: "20:00",
    notify: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [habitsRes, todayRes, statsRes] = await Promise.all([
        api.get("/habits"),
        api.get("/habits/today"),
        api.get("/habits/stats/summary")
      ]);
      setHabits(habitsRes.data.habits || []);
      setTodayHabits(todayRes.data.habits || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching habits:", error);
      toast.error("Error al cargar hábitos");
    } finally {
      setLoading(false);
    }
  };

  const openNewHabitDialog = () => {
    setForm({
      name: "",
      description: "",
      frequency: "daily",
      custom_days: [],
      reminder_time: "20:00",
      notify: true
    });
    setEditingHabit(null);
    setDialogOpen(true);
  };

  const openEditDialog = (habit) => {
    setForm({
      name: habit.name,
      description: habit.description || "",
      frequency: habit.frequency,
      custom_days: habit.custom_days || [],
      reminder_time: habit.reminder_time || "20:00",
      notify: habit.notify !== false
    });
    setEditingHabit(habit);
    setDialogOpen(true);
  };

  const toggleCustomDay = (day) => {
    setForm(prev => ({
      ...prev,
      custom_days: prev.custom_days.includes(day)
        ? prev.custom_days.filter(d => d !== day)
        : [...prev.custom_days, day]
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    setSubmitting(true);
    try {
      if (editingHabit) {
        await api.put(`/habits/${editingHabit.id}`, form);
        toast.success("Hábito actualizado");
      } else {
        await api.post("/habits", form);
        toast.success("Hábito creado");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (habitId) => {
    if (!confirm("¿Eliminar este hábito?")) return;
    
    try {
      await api.delete(`/habits/${habitId}`);
      toast.success("Hábito eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const toggleCompletion = async (habit) => {
    try {
      const isCompleted = habit.completed_today;
      await api.post(`/habits/${habit.id}/complete`, { completed: !isCompleted });
      
      // Update local state immediately for better UX
      setTodayHabits(prev => prev.map(h => 
        h.id === habit.id ? { ...h, completed_today: !isCompleted } : h
      ));
      
      if (!isCompleted) {
        toast.success(`✅ ${habit.name} completado`);
      }
      
      // Refresh stats
      const statsRes = await api.get("/habits/stats/summary");
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const getFrequencyLabel = (freq, customDays) => {
    if (freq === "custom" && customDays?.length) {
      return customDays.map(d => DAYS_OF_WEEK.find(day => day.value === d)?.short).join(", ");
    }
    return FREQUENCY_OPTIONS.find(f => f.value === freq)?.label || freq;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#04f198]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="habits-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-slate-900 dark:text-white">
            Hábitos y Rutinas
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Construye hábitos positivos día a día
          </p>
        </div>
        <Button 
          onClick={openNewHabitDialog} 
          className="bg-cyan-700 hover:bg-cyan-800 text-white"
          data-testid="new-habit-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Hábito
        </Button>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-[#04f198]/10 to-[#42bcf5]/10 border-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#04f198]/20 rounded-xl">
                  <Flame className="w-5 h-5 text-[#04f198]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stats.current_streak}
                  </p>
                  <p className="text-xs text-slate-500">Racha actual</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stats.today_completed}/{stats.habits_due_today || 0}
                  </p>
                  <p className="text-xs text-slate-500">Completados hoy</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-xl">
                  <BarChart3 className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stats.weekly_completion_rate}%
                  </p>
                  <p className="text-xs text-slate-500">Esta semana</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/20 rounded-xl">
                  <Award className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {stats.monthly_completion_rate}%
                  </p>
                  <p className="text-xs text-slate-500">Este mes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Today's Habits */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-[#04f198]" />
          Hábitos de Hoy
        </h2>

        {todayHabits.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No tienes hábitos para hoy</p>
            <Button 
              variant="link" 
              className="text-cyan-700"
              onClick={openNewHabitDialog}
            >
              Crear tu primer hábito
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {todayHabits.map(habit => (
              <div 
                key={habit.id}
                onClick={() => toggleCompletion(habit)}
                className={`
                  p-4 rounded-xl border-2 cursor-pointer transition-all
                  ${habit.completed_today 
                    ? 'bg-[#04f198]/10 border-[#04f198] dark:bg-[#04f198]/5' 
                    : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-[#04f198]/50'}
                `}
              >
                <div className="flex items-center gap-4">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center transition-all
                    ${habit.completed_today 
                      ? 'bg-[#04f198] text-white' 
                      : 'bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-600'}
                  `}>
                    {habit.completed_today && <Check className="w-5 h-5" />}
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-medium ${habit.completed_today ? 'text-[#04f198] line-through' : 'text-slate-900 dark:text-white'}`}>
                      {habit.name}
                    </h3>
                    {habit.description && (
                      <p className="text-sm text-slate-500">{habit.description}</p>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(habit); }}>
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={(e) => { e.stopPropagation(); handleDelete(habit.id); }}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Progress bar */}
        {todayHabits.length > 0 && (
          <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-500">Progreso de hoy</span>
              <span className="font-medium text-[#04f198]">
                {todayHabits.filter(h => h.completed_today).length} de {todayHabits.length}
              </span>
            </div>
            <Progress 
              value={(todayHabits.filter(h => h.completed_today).length / todayHabits.length) * 100}
              className="h-2"
            />
          </div>
        )}
      </div>

      {/* All Habits */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
          Todos los Hábitos
        </h2>

        {habits.length === 0 ? (
          <p className="text-center py-4 text-slate-500">Sin hábitos configurados</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {habits.map(habit => (
              <div 
                key={habit.id}
                className={`p-4 rounded-xl border ${habit.is_active !== false ? 'border-slate-200 dark:border-slate-700' : 'border-slate-100 dark:border-slate-800 opacity-50'}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-medium text-slate-900 dark:text-white">{habit.name}</h3>
                    <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                      <Calendar className="w-3 h-3" />
                      {getFrequencyLabel(habit.frequency, habit.custom_days)}
                    </p>
                    <p className="text-sm text-slate-500 flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      {habit.reminder_time || "Sin recordatorio"}
                      {habit.notify && <Bell className="w-3 h-3 text-[#04f198]" />}
                    </p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(habit)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDelete(habit.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Habit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col w-[95vw] sm:w-full overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-['Outfit']">
              {editingHabit ? 'Editar Hábito' : 'Nuevo Hábito'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 overflow-y-auto flex-1 px-1"
            style={{ overflowX: 'hidden' }}
          >
            <div>
              <Label>Nombre del hábito *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Meditar 10 minutos"
                data-testid="habit-name-input"
              />
            </div>

            <div>
              <Label>Descripción (opcional)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detalles adicionales..."
                rows={2}
              />
            </div>

            <div>
              <Label>Frecuencia</Label>
              <Select 
                value={form.frequency} 
                onValueChange={(v) => setForm(prev => ({ ...prev, frequency: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.frequency === "custom" && (
              <div>
                <Label>Días de la semana</Label>
                <div className="flex gap-2 mt-2">
                  {DAYS_OF_WEEK.map(day => (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleCustomDay(day.value)}
                      className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                        form.custom_days.includes(day.value)
                          ? 'bg-[#04f198] text-white'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200'
                      }`}
                    >
                      {day.short}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>Hora de recordatorio</Label>
              <Input
                type="time"
                value={form.reminder_time}
                onChange={(e) => setForm(prev => ({ ...prev, reminder_time: e.target.value }))}
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-slate-500" />
                <Label>Notificar por WhatsApp</Label>
              </div>
              <Switch
                checked={form.notify}
                onCheckedChange={(v) => setForm(prev => ({ ...prev, notify: v }))}
              />
            </div>
          </div>

          <DialogFooter className="mt-4 flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={submitting}
              className="bg-cyan-700 hover:bg-cyan-800"
              data-testid="save-habit-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingHabit ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HabitsPage;
